/* =====================================================================
 * SidebarUsageSection — 사이드바 하단 사용량(토큰 / 스토리지) 바
 *
 * Purely presentational: the caller computes limits + warn thresholds
 * (they differ per-team vs. per-user) and passes the derived values in.
 * Keeping the component dumb makes it trivial to swap in a different
 * compute strategy for B2B tenants (e.g. per-seat quotas) without
 * rewriting the visual layout.
 * ===================================================================== */

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { formatTokens, formatBytes } from '@auw/workspace-utils';

export interface SidebarUsageSectionProps {
  /** true → `selectedTeamName` is rendered in the title */
  isTeamView: boolean;
  /** shown as "<name> 사용량" when `isTeamView` is true */
  selectedTeamName?: string;
  /**
   * When false (team view or premium user), hides the upgrade CTA.
   * Accepts a pre-computed label so callers can tune wording
   * ("Upgrade" vs "Change plan") without re-reading the user store.
   */
  upgradeLabel?: string;
  onClick?: () => void;

  displayTokenRemain: number;
  displayTokenTotal: number;
  tokenRemainPct: number;
  tokenBarColor: string;

  displayStorageUsed: number;
  displayStorageTotal: number;
  storageUsedPct: number;
  storageBarColor: string;
}

export const SidebarUsageSection: React.FC<SidebarUsageSectionProps> = ({
  isTeamView,
  selectedTeamName,
  upgradeLabel,
  onClick,
  displayTokenRemain,
  displayTokenTotal,
  tokenRemainPct,
  tokenBarColor,
  displayStorageUsed,
  displayStorageTotal,
  storageUsedPct,
  storageBarColor,
}) => {
  const { t } = useTranslation('common');

  return (
    <Section onClick={onClick}>
      <Header>
        <Title>
          {isTeamView && selectedTeamName
            ? `${selectedTeamName} ${t('sidebar.usage')}`
            : t('sidebar.usage')}
        </Title>
        {!isTeamView && upgradeLabel && <Upgrade>{upgradeLabel}</Upgrade>}
      </Header>
      <Row>
        <Label>
          <LabelName>{t('sidebar.tokens')}</LabelName>
          <LabelValue $warn={tokenRemainPct < 20}>
            {formatTokens(displayTokenRemain)} / {formatTokens(displayTokenTotal)}
          </LabelValue>
        </Label>
        <BarTrack>
          <BarFill $pct={tokenRemainPct} $color={tokenBarColor} />
        </BarTrack>
      </Row>
      <Row>
        <Label>
          <LabelName>{t('sidebar.storageLabel')}</LabelName>
          <LabelValue $warn={storageUsedPct > 80}>
            {formatBytes(displayStorageUsed)} / {formatBytes(displayStorageTotal)}
          </LabelValue>
        </Label>
        <BarTrack>
          <BarFill $pct={storageUsedPct} $color={storageBarColor} />
        </BarTrack>
      </Row>
    </Section>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Section = styled.div`
  padding: 10px 12px 8px;
  margin: 6px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease-out, box-shadow 0.15s ease-out;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 3px rgba(0,0,0,0.25), inset 0 0.5px 0 rgba(255,255,255,0.03)'
      : '0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.02)'};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const Title = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
`;

const Upgrade = styled.span`
  font-size: 0.625rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
  opacity: 0.85;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${Section}:hover & {
    opacity: 1;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const LabelName = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LabelValue = styled.span<{ $warn?: boolean }>`
  font-size: 0.625rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${({ $warn, theme }) =>
    $warn ? theme.colors.error : theme.colors.textMuted};
`;

const BarTrack = styled.div`
  width: 100%;
  height: 3px;
  border-radius: 1.5px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'inset 0 0.5px 1px rgba(0,0,0,0.15)'
      : 'inset 0 0.5px 1px rgba(0,0,0,0.06)'};
  overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  border-radius: 1.5px;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $color }) => $color};
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;
